'use client';

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useCountry } from '@/context/CountryContext';
import { useLanguage } from '@/context/LanguageContext';

interface TooltipPayload {
  name: string;
  value: number;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}

// Map English month abbreviations to French
const frMonthMap: Record<string, string> = {
  'Aug': 'août',
  'Sep': 'sept',
  'Oct': 'oct',
  'Nov': 'nov',
  'Dec': 'déc',
  'Jan': 'janv',
  'Feb': 'févr',
  'Mar': 'mars',
  'Apr': 'avr',
  'May': 'mai',
  'Jun': 'juin',
  'Jul': 'juil',
};

function translateMonth(monthStr: string, lang: string): string {
  if (lang !== 'fr') return monthStr;
  // Format: "Aug 25" -> "août 25"
  const parts = monthStr.split(' ');
  if (parts.length === 2) {
    const fr = frMonthMap[parts[0]];
    return fr ? `${fr} ${parts[1]}` : monthStr;
  }
  return monthStr;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-surface-card border border-surface-border rounded-lg px-3 py-2 shadow-card-md text-xs">
      <p className="font-semibold text-text-main mb-1.5">{label}</p>
      {payload.map((p) => (
        <div key={`tip-${p.name}`} className="flex items-center gap-2 mb-0.5">
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: p.color }}
          />
          <span className="text-text-muted">{p.name}:</span>
          <span className="font-semibold text-text-main tabular-nums">
            {p.value.toLocaleString('en-US')}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function ConnectionsLineChart() {
  const { connections } = useCountry();
  const { t, lang } = useLanguage();

  // Map month labels to French at render time without changing underlying data
  const chartData = connections.map((d) => ({
    ...d,
    month: translateMonth(d.month, lang),
  }));

  const newConnectionsLabel = lang === 'fr' ? 'Nouvelles connexions' : 'New Connections';
  const cumulativeLabel = lang === 'fr' ? 'Total cumulé' : 'Cumulative Total';

  return (
    <div className="h-[280px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 8, left: -10, bottom: 0 }}
        >
          <defs>
            <linearGradient id="lineGold" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="var(--accent)" />
              <stop offset="100%" stopColor="var(--accent)" />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--border)"
            vertical={false}
          />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="new"
            orientation="left"
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => v.toLocaleString('en-US')}
            width={50}
          />
          <YAxis
            yAxisId="cum"
            orientation="right"
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) =>
              v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toString()
            }
            width={45}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
            iconType="circle"
            iconSize={8}
          />
          <Line
            yAxisId="new"
            type="monotone"
            dataKey="newConnections"
            name={newConnectionsLabel}
            stroke="var(--accent)"
            strokeWidth={2.5}
            dot={{ fill: 'var(--accent)', r: 3, strokeWidth: 0 }}
            activeDot={{ r: 5, strokeWidth: 0 }}
          />
          <Line
            yAxisId="cum"
            type="monotone"
            dataKey="cumulative"
            name={cumulativeLabel}
            stroke="var(--primary)"
            strokeWidth={2}
            strokeDasharray="5 3"
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0, fill: 'var(--primary)' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}